import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { FormRow } from '@tanium/react-form';
import { Button, TooltippedButton } from '@tanium/react-button';
import { Box } from '@tanium/react-box';
import { Notification } from '@tanium/react-persistent-notifications';
import { Stack } from '@tanium/react-stack';
import { LoginFormTextInput, LoginFormButton } from './LoginFormComponents';
import { useAsync, AsyncStatus } from '../../hooks/useAsync';
import { useOnInputChange } from '../../components/InputHandlerUtils';
import { Api } from '../../api';

export interface ForgotUsernameProps {
  switchToLocalSignin: () => void;
  switchToCreateAccount: () => void;
}

export const ForgotUsername: React.VFC<ForgotUsernameProps> = ({ switchToLocalSignin, switchToCreateAccount }) => {
  const { t } = useTranslation();
  const [email, setEmail] = useState('');
  const {
    execute: sendEmail,
    status: sendEmailStatus,
    reset: resetEmailSender,
  } = useAsync(() => {
    return Api.tenants.sendRecoverUsernameEmail(email);
  });

  const handleEmailChange = useOnInputChange(setEmail);

  const handleSubmit = useCallback<React.FormEventHandler>(
    async (event) => {
      resetEmailSender();
      event.preventDefault();
      await sendEmail();
    },
    [sendEmail, resetEmailSender]
  );

  const tooltipRecoverUsername = t(
    'Enter the email address for your account to receive an email containing usernames associated with that address.'
  );
  const tooltipCreateAccount = t(
    'Intended for On Premises Customers. For cloud customers, please contact Tanium Support.'
  );

  return (
    <Box style={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      <Box style={{ flexGrow: 1, display: 'flex', alignItems: 'center', width: '100%' }}>
        <form style={{ width: '100%' }} onSubmit={handleSubmit}>
          {sendEmailStatus === AsyncStatus.Success && (
            <Box mb={3}>
              <Notification role="status" type="success">
                {t(
                  'If the email matches an account, any associated usernames have been sent to the specified email address'
                )}
              </Notification>
            </Box>
          )}
          {sendEmailStatus === AsyncStatus.Failure && (
            <Box mb={3}>
              <Notification role="status" type="error">
                {t('Failed to send username recovery email')}
              </Notification>
            </Box>
          )}
          {sendEmailStatus !== AsyncStatus.Success && (
            <>
              <FormRow label={t('Email')} dataKey="email">
                <LoginFormTextInput type="email" value={email} onChange={handleEmailChange} required />
              </FormRow>
              <LoginFormButton
                loading={sendEmailStatus === AsyncStatus.Pending}
                tooltipContent={tooltipRecoverUsername}
              >
                {t('Recover Username')}
              </LoginFormButton>
              <TooltippedButton
                style={{ width: '100%', marginTop: '10px' }}
                onClick={switchToCreateAccount}
                tooltipContent={tooltipCreateAccount}
              >
                {t('Create Account')}
              </TooltippedButton>
            </>
          )}
          {sendEmailStatus === AsyncStatus.Success && (
            <Button style={{ width: '100%' }} onClick={switchToLocalSignin}>
              {t('Sign In')}
            </Button>
          )}
        </form>
      </Box>
      <Stack justifyContent="center" style={{ margin: 'auto 0 10px 0' }}>
        {sendEmailStatus === AsyncStatus.Success && (
          <Button variant="link" onClick={resetEmailSender}>
            {t('Retry')}
          </Button>
        )}
        {sendEmailStatus !== AsyncStatus.Success && (
          <Button variant="link" onClick={switchToLocalSignin}>
            {t('Sign In')}
          </Button>
        )}
      </Stack>
    </Box>
  );
};
